import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardText, CardFooter } from 'reactstrap';
import PropTypes from 'prop-types';
import { getGoogleReviewConfig, deleteGoogleReviewConfig } from '../../axios/businessReviews';
import { useAuthStore } from '../../zustand-store/auth.store';
import GoogleReviewDetailModal from './googleReviewDetailModal';
import DeleteModal from './DeleteModal';
import { toast } from 'react-toastify';

const GoogleReviewCard = ({ title, description, icon }) => {
  const locationId = useAuthStore((state) => state.locationId);
  const [loader, setLoader] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const toggleModal = () => setModalOpen((prev) => !prev);

  useEffect(() => {
    if (!locationId) return;
    let isMounted = true;
    const checkConnected = async () => {
      setLoader(true);
      try {
        const response = await getGoogleReviewConfig(locationId);
        if (!isMounted) return;
        const { googleMapUrl, googlePlaceId } = response?.data?.data || {};
        setIsConnected(!!googlePlaceId);
      } catch (error) {
        if (isMounted) setIsConnected(false);
      } finally {
        if (isMounted) setLoader(false);
      }
    };
    checkConnected();
    return () => {
      isMounted = false;
    };
  }, [locationId, modalOpen]);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const response = await deleteGoogleReviewConfig(locationId);
      if (response?.status === 200 || response?.status === 201) {
        toast.success('Google Reviews disconnected successfully');
        setIsConnected(false);
        setDisconnectModalOpen(false);
      }
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <>
      <Card
        style={{ border: '1px solid', color: '#EFF2F7', cursor: 'pointer', height: 250 }}
        onClick={toggleModal}>
        <CardBody>
          <div className="d-flex justify-content-between">
            <img style={{ width: '30px', height: '30px' }} src={icon} alt="" />
            {loader ? (
              <span className="text-warning">Loading...</span>
            ) : isConnected ? (
              <span
                className="bg-success text-white"
                style={{
                  fontSize: 13,
                  color: '#000',
                  background: '#F2F2F2',
                  padding: 6,
                  paddingRight: 16,
                  paddingLeft: 16,
                  borderRadius: 123
                }}>
                <i className="mdi mdi-check" style={{ paddingLeft: 10, paddingRight: 9 }} />
                Connected
              </span>
            ) : (
              <span className="text-primary">Connect</span>
            )}
          </div>
          <p
            style={{ fontSize: '13px', fontWeight: '700', color: '#000', marginTop: 14 }}
            className="mt-2">
            {title}
          </p>
          <CardText style={{ color: '#000', fontSize: '13px', fontWeight: '400', marginTop: 8 }}>
            {description}
          </CardText>
        </CardBody>
        <CardFooter
          style={{ minHeight: '48px' }}
          className="d-flex align-items-center justify-content-end py-2"
          onClick={(e) => e.stopPropagation()}>
          {isConnected ? (
            <span
              className="text-danger"
              style={{ cursor: 'pointer', fontSize: 13 }}
              onClick={() => setDisconnectModalOpen(true)}>
              Disconnect
            </span>
          ) : (
            <span className="invisible">Disconnect</span>
          )}
        </CardFooter>
      </Card>
      <GoogleReviewDetailModal
        isOpen={modalOpen}
        toggle={toggleModal}
        title={title}
        icon={icon}
        description={description}
      />
      <DeleteModal
        show={disconnectModalOpen}
        isDeleting={disconnecting}
        heading="Are you sure you want to disconnect Google Reviews? Your customers will no longer see Google reviews on your booking page."
        componentName="Google Reviews"
        buttonTitle="Disconnect"
        onDeleteClick={handleDisconnect}
        onCloseClick={() => setDisconnectModalOpen(false)}
      />
    </>
  );
};

GoogleReviewCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.any.isRequired
};

export default GoogleReviewCard;
